import apiClient from './api.service';
import {
  CreateStoreData,
  CreateStoreFromTemplateData,
  UpdateStoreData,
  CreateInvitationData,
  StoreResponse
} from '../types/store.types';
import { StoreSetupDraft, StoreSetupPreviewResponse } from '../types/storeSetup.types';

/**
 * Store Service
 * Handles all store-related API calls
 */

class StoreService {
  /**
   * Get all stores for current user
   * @returns Promise with stores
   */
  async getUserStores(): Promise<StoreResponse> {
    const response = await apiClient.get<StoreResponse>('/stores');
    return response.data;
  }

  /**
   * Get a specific store by ID
   * @param id - Store ID
   * @returns Promise with store data
   */
  async getStoreById(id: number): Promise<StoreResponse> {
    const response = await apiClient.get<StoreResponse>(`/stores/${id}`);
    return response.data;
  }

  /**
   * Create a new store
   * @param data - Store data
   * @returns Promise with created store
   */
  async createStore(data: CreateStoreData): Promise<StoreResponse> {
    const response = await apiClient.post<StoreResponse>('/stores', data);
    return response.data;
  }

  /**
   * Brouillon de setup, rien n'est enregistré.
   */
  async previewSetup(message: string, setup: StoreSetupDraft): Promise<StoreSetupPreviewResponse> {
    const response = await apiClient.post<StoreSetupPreviewResponse>('/stores/setup/preview', {
      message,
      setup
    });
    return response.data;
  }

  /**
   * Create a new store from template
   * @param data - Store data
   * @returns Promise with created store
   */
  async createStoreFromTemplate(data: CreateStoreFromTemplateData): Promise<StoreResponse> {
    const response = await apiClient.post<StoreResponse>('/stores/from-template', data);
    return response.data;
  }

  /**
   * Get published templates available for store creation
   */
  async getPublishedTemplates(): Promise<StoreResponse> {
    const response = await apiClient.get<StoreResponse>('/stores/templates');
    return response.data;
  }

  /**
   * Aperçu détaillé (lecture seule) d'un template publié — listes de paramétrage pour la modale UI.
   */
  async getPublishedTemplatePreview(templateStoreId: number): Promise<StoreResponse> {
    const response = await apiClient.get<StoreResponse>(
      `/stores/templates/${templateStoreId}/preview`
    );
    return response.data;
  }

  /**
   * Update a store
   * @param id - Store ID
   * @param data - Updated store data
   * @returns Promise with updated store
   */
  async updateStore(id: number, data: UpdateStoreData): Promise<StoreResponse> {
    const response = await apiClient.put<StoreResponse>(`/stores/${id}`, data);
    return response.data;
  }

  /**
   * Delete a store
   * @param id - Store ID
   * @returns Promise
   */
  async deleteStore(id: number): Promise<StoreResponse> {
    const response = await apiClient.delete<StoreResponse>(`/stores/${id}`);
    return response.data;
  }

  /**
   * Join a store using invitation token
   * @param invitationToken - Invitation token
   * @returns Promise with store data
   */
  async joinStore(invitationToken: string): Promise<StoreResponse> {
    const response = await apiClient.post<StoreResponse>('/stores/join', { invitationToken });
    return response.data;
  }

  /**
   * Create an invitation for a store
   * @param storeId - Store ID
   * @param data - Invitation data
   * @returns Promise with created invitation
   */
  async createInvitation(storeId: number, data: CreateInvitationData): Promise<StoreResponse> {
    const response = await apiClient.post<StoreResponse>(`/stores/${storeId}/invitations`, data);
    return response.data;
  }

  /**
   * Get all invitations for a store
   * @param storeId - Store ID
   * @returns Promise with invitations
   */
  async getInvitations(storeId: number): Promise<StoreResponse> {
    const response = await apiClient.get<StoreResponse>(`/stores/${storeId}/invitations`);
    return response.data;
  }

  /**
   * Deactivate an invitation
   * @param storeId - Store ID
   * @param invitationId - Invitation ID
   * @returns Promise
   */
  async deactivateInvitation(storeId: number, invitationId: number): Promise<StoreResponse> {
    const response = await apiClient.delete<StoreResponse>(`/stores/${storeId}/invitations/${invitationId}`);
    return response.data;
  }

  /**
   * Get all users of a store
   * @param storeId - Store ID
   * @returns Promise with store users
   */
  async getStoreUsers(storeId: number): Promise<StoreResponse> {
    const response = await apiClient.get<StoreResponse>(`/stores/${storeId}/users`);
    return response.data;
  }

  /**
   * Save current store to localStorage
   * @param store - Store object
   */
  saveCurrentStore(store: any): void {
    localStorage.setItem('currentStore', JSON.stringify(store));
  }

  /**
   * Get current store from localStorage
   * @returns Store object or null
   */
  getCurrentStore(): any {
    const storeStr = localStorage.getItem('currentStore');
    return storeStr ? JSON.parse(storeStr) : null;
  }

  /**
   * Clear current store from localStorage
   */
  clearCurrentStore(): void {
    localStorage.removeItem('currentStore');
  }
}

export default new StoreService();

